import { FormHeader } from 'components/common';
import {
  useBroadcastApproveQuery,
  useBroadcastConditionalReviewQuery,
  useBroadcastCreateQuery,
  useBroadcastDeleteQuery,
  useBroadcastRejectQuery,
  useBroadcastReopenQuery,
  useBroadcastReviewQuery,
  useBroadcastUpdateQuery,
} from 'hooks';
import { validateHTML } from 'hooks/helpers/common';
import { useModal } from 'hooks/ui/useModal';
import { BroadcastStatuses } from 'interfaces';
import { useFeatureTogglesStore } from 'store/feature-toggling';
import {
  DraftBroadcastValidationField,
  TAB_STATUSES,
  setBroadcastErrors,
  useBroadcastStore,
  validateBroadcast,
  validateTitle,
} from 'store/broadcast';

export const BroadcastHeader = () => {
  const modal = useModal();
  const {
    draft,
    actions: { setActiveTab },
  } = useBroadcastStore((state) => state);
  const { isFeatureEnabled } = useFeatureTogglesStore();
  const isBroadcastTagsEnabled = isFeatureEnabled('broadcastTags');
  const { mutate: createBroadcast } = useBroadcastCreateQuery({
    keepActive: true,
  });
  const { mutate: updateBroadcast } = useBroadcastUpdateQuery();
  const { mutate: sendBroadcastForReview } = useBroadcastReviewQuery();
  const { mutate: sendBroadcastWithCondition } =
    useBroadcastConditionalReviewQuery();
  const { mutate: approveBroadcast } = useBroadcastApproveQuery();
  const { mutate: rejectBroadcast } = useBroadcastRejectQuery();
  const { mutate: reopenBroadcast } = useBroadcastReopenQuery();
  const { mutate: deleteBroadcast, discardDraft } = useBroadcastDeleteQuery();

  const validateAllFields = (
    fullValidation = false,
    validationOnApprove = false,
  ) => {
    const broadcastErrors = validateBroadcast({
      broadcast: draft,
      fullValidation,
      validationOnApprove,
      isBroadcastTagsEnabled,
    });
    const hasError = Object.values(broadcastErrors).some((err) => err);

    if (hasError) {
      setBroadcastErrors(broadcastErrors);
    }

    return hasError;
  };

  const handleSaveAsDraft = () => {
    const draftTitle = draft.title.trim();
    const titleError = validateTitle(draftTitle, true);

    if (titleError) {
      return setBroadcastErrors({
        title: titleError,
      } as DraftBroadcastValidationField);
    }

    const hasError = validateAllFields(false);

    if (hasError) return null;

    return modal.confirm({
      text: `Are you sure you want to save "${validateHTML(
        draft.title,
      )}" as a Draft?`,
      highlights: [`"${validateHTML(draft.title)}"`, 'save', 'Draft'],
      successAction: 'Yes',
      onSuccess: () => {
        if (draft.id) {
          return updateBroadcast({
            id: draft.id,
            broadcast: draft,
          });
        }

        return createBroadcast(draft);
      },
    });
  };

  const handleSaveForApprove = () => {
    const hasError = validateAllFields(true);

    if (hasError) return null;

    return modal.confirm({
      text: `Are you sure you want to save "${validateHTML(
        draft.title,
      )}" for Approve?
      You will not be able to edit it anymore.`,
      highlights: [`"${validateHTML(draft.title)}"`, 'save', 'Approve'],
      successAction: 'Yes',
      onSuccess: () => {
        if (draft.status === BroadcastStatuses.NOT_SAVED) {
          return sendBroadcastWithCondition(draft);
        }

        return sendBroadcastForReview(draft.id);
      },
    });
  };

  const handleReject = () =>
    modal.confirm({
      text: `Are you sure you want to Reject "${validateHTML(draft.title)}"?
      This action will reopen broadcast for editing.`,
      highlights: [`"${validateHTML(draft.title)}"`, 'Reject'],
      successAction: 'Yes',
      onSuccess: () => rejectBroadcast(draft.id),
    });

  const handleApprove = () => {
    const hasError = validateAllFields(true, true);

    if (!hasError) {
      modal.confirm({
        text: `Are you sure you want to Approve "${validateHTML(
          draft.title,
        )}"?`,
        highlights: [`"${validateHTML(draft.title)}"`, 'Approve'],
        successAction: 'Yes',
        onSuccess: () => approveBroadcast(draft.id),
      });
    }
  };

  const handleSendToRework = () =>
    modal.confirm({
      text: `Are you sure you want to Reopen "${validateHTML(draft.title)}"?`,
      highlights: [`"${validateHTML(draft.title)}"`, 'Reopen'],
      successAction: 'Yes',
      onSuccess: () => {
        reopenBroadcast(draft.id);
        setActiveTab(TAB_STATUSES.IN_PROGRESS);
      },
    });

  const handleDelete = () => {
    if (draft.id) {
      return modal.delete({
        text: `Are you sure you want to Delete ${
          draft.title ? `"${validateHTML(draft.title)}"` : 'this broadcast'
        }?`,
        highlights: [
          ` ${
            draft.title ? `"${validateHTML(draft.title)}"` : 'this broadcast'
          }`,
          'Delete',
        ],
        onSuccess: () => deleteBroadcast(draft.id),
      });
    }

    return modal.delete({
      text: 'Are you sure you want to Delete this draft?',
      highlights: ['Delete', 'draft'],
      onSuccess: () => discardDraft(),
    });
  };

  return (
    <FormHeader
      draft={draft}
      handleApprove={handleApprove}
      handleDelete={handleDelete}
      handleReject={handleReject}
      handleSaveAsDraft={handleSaveAsDraft}
      handleSaveForApprove={handleSaveForApprove}
      handleSendToRework={handleSendToRework}
    />
  );
};
